import { ref, computed, watch } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import store from "@/store";
import ToastificationContent
  from "@core/components/toastification/ToastificationContent.vue";

export default function useDmpPanelsWizard(props, { emit }) {
  const toast = useToast();
  const { t } = useI18nUtils();

  const currentStep = ref(0);
  const finishAtStep = ref(0);
  const isLoading = ref(false);
  const isCreatingPanel = ref(false);
  const isSyncingDevices = ref(false);
  const profileOptions = ref([]);
  const wizardTitle = computed(() => {
    return props.isAdd ? t("dmp.wizard.add.title") : t("dmp.wizard.edit.title");
  });

  const wizardSteps = [
    t("dmp.wizard.steps.general"),
  ];

  const wizardStepIcons = ["SettingsIcon"];
  const blankData = {
    uuid: null,
    name: null,
    panel_host: null,
    instance_host: null,
    panel_model: null,
    panel_listening_port: null,
    contact_id_number: null,
    client_password: null,
    remote_cfg_password: null,
    id_unit: null,
    id_center: null,
    id_sector: null,
    id_dmp_profile: null,
    virtual_bypass: null,
    enabled_zones_audit: false,
    days_zones_audit: null,
    enabled_areas_audit: false,
    days_areas_audit: null,
    id_additional_router: null,
  };

  const itemData = ref(JSON.parse(JSON.stringify(blankData)));
  const originalData = ref(JSON.parse(JSON.stringify(blankData)));
  const hasChanges = computed(() => {
    if (props.isAdd) return false;
    return JSON.stringify(itemData.value) !== JSON.stringify(originalData.value);
  });

  const additionalRouters = ref([])

  const resetData = () => {
    itemData.value = JSON.parse(JSON.stringify(blankData));
    currentStep.value = 0;
  };

  const closeWizard = () => {
    emit("update:isActive", false);
    currentStep.value = 0;
    if (props.isAdd) {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      emit("update:itemEdit", {});
    }
    emit("close-wizard");
  };

  const prevStep = () => {
    if (currentStep.value > 0) {
      currentStep.value--;
    }
    emit("prev-step");
  };

  const nextStep = async () => {
    if (currentStep.value < finishAtStep.value) {
      currentStep.value++;
    }
    emit("next-step");
  };

  const createItem = async () => {
    const saveData = prepareDataForSave();

    isCreatingPanel.value = true;

    try {
      const response = await store.dispatch("dmp_panels/add", saveData);

      if (response && response.data) {
        itemData.value.uuid = response.data.uuid || response.data.id;
        itemData.value.id = response.data.id;

        originalData.value = JSON.parse(JSON.stringify(itemData.value));

        toast({
          component: ToastificationContent,
          props: {
            title: "Panel created successfully",
            text: "Panel created successfully.",
            icon: "CheckIcon",
            variant: "success",
          },
        });
        emit("refetch-data");

        return true;
      } else {
        throw new Error("No se recibió respuesta válida del servidor");
      }
    } catch (error) {
      console.error("Error creating item:", error);
      toast({
        component: ToastificationContent,
        props: {
          title: "Error creating panel",
          text: error.response?.data?.message || "Error creating panel. Please try again.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
      return false;
    } finally {
      isCreatingPanel.value = false;
    }
  };

  const updatePanelBeforeSync = async () => {
    if (!itemData.value.id) {
      console.error("No panel ID available for update");
      return false;
    }

    const saveData = prepareDataForSave();
    const req = {
      id: itemData.value.id,
      params: saveData,
    };

    try {
      const response = await store.dispatch("dmp_panels/edit", req);

      if (response && response.data) {
        originalData.value = JSON.parse(JSON.stringify(itemData.value));

        toast({
          component: ToastificationContent,
          props: {
            title: "Panel updated successfully",
            text: props.isAdd ? "Panel data updated before retrying synchronization." : "Panel data updated before synchronization.",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        emit("refetch-data");

        return true;
      } else {
        throw new Error("No se recibió respuesta válida del servidor");
      }
    } catch (error) {
      console.error("Error updating panel:", error);
      toast({
        component: ToastificationContent,
        props: {
          title: "Error updating panel",
          text: error.response?.data?.message || "Error updating panel. Please check your data.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
      return false;
    }
  };

  const syncDevices = async () => {
    if (!itemData.value.id) {
      console.error("No panel ID available for sync");
      return false;
    }

    isSyncingDevices.value = true;

    try {
      const syncResponse = await store.dispatch("dmp_panels/syncAllPanelData", itemData.value.id);

      if (syncResponse && syncResponse.success !== false) {
        toast({
          component: ToastificationContent,
          props: {
            title: "Devices synchronized",
            text: "Panel devices synchronized successfully.",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        emit("refetch-data");

        setTimeout(() => {
          emit("update:isActive", false);
          emit("close-wizard");
        }, 1000);

        return true;
      } else {
        throw new Error(syncResponse?.message || "Failed to synchronize devices");
      }
    } catch (syncError) {
      console.error("Error synchronizing devices:", syncError);
      toast({
        component: ToastificationContent,
        props: {
          title: "Sync devices failed",
          text: syncError.response?.data?.message || syncError.message || "Failed to synchronize devices.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
      return false;
    } finally {
      isSyncingDevices.value = false;
    }
  };

  const createItemAndSync = async () => {
    if (props.isAdd) {
      const created = await createItem();
      if (created) {
        closeWizard();
      }
    } else {
      if (hasChanges.value) {
        const updated = await updatePanelBeforeSync();
        if (updated) {
          closeWizard();
        }
      } else {
        closeWizard();
      }
    }
  };

  const handleRetrySync = async () => {
    if (props.isAdd && itemData.value.id) {
      const updateSuccess = await updatePanelBeforeSync();
      if (!updateSuccess) {
        return false;
      }
    } else if (hasChanges.value) {
      const updateSuccess = await updatePanelBeforeSync();
      if (!updateSuccess) {
        return false;
      }
    }

    return await syncDevices();
  };

  const loadData = () => {
    isLoading.value = true;
    additionalRouters.value = [];
    
    if (!props.isAdd) {
      itemData.value = props.itemEdit;
      originalData.value = JSON.parse(JSON.stringify(props.itemEdit));
      itemData.value.virtual_bypass = !!props.itemEdit.virtual_bypass;
      originalData.value = JSON.parse(JSON.stringify(itemData.value));

      itemData.value.id_additional_router = props.itemEdit.camera_proxy_router?.camera?.id || null;

    } else {
      itemData.value.id_unit = props?.itemEdit?.id_unit || null;
    }

    getProfiles();

    if (props.itemEdit?.device?.units) {
      itemData.value.id_unit = props.itemEdit.device.units.map((value) => value.id);
      
      additionalRouters.value = props.itemEdit.device.units.map(u => {
        return (u.additional_routers ?? []).map(r => r.camera)
      }).flat()

      if (!props.isAdd) {
        originalData.value.id_unit = itemData.value.id_unit;
      }
    }
    isLoading.value = false;
  };

  const prepareDataForSave = () => {
    const saveData = { ...itemData.value };

    if (saveData.id_unit && Array.isArray(saveData.id_unit)) {
      saveData.id_unit = saveData.id_unit.map((unit) =>
        typeof unit === 'object' ? unit.value : unit
      );
    }

    return saveData;
  };

  const handleSave = () => {
    const saveData = prepareDataForSave();

    let dispatch;
    if (props.isAdd) {
      dispatch = store.dispatch("dmp_panels/add", saveData);
    } else {
      const req = {
        id: itemData.value.id,
        params: saveData,
      };
      dispatch = store.dispatch("dmp_panels/edit", req);
    }

    isLoading.value = true;

    dispatch
      .then((response) => {
        toast({
          component: ToastificationContent,
          props: {
            title: props.isAdd ? "Agregado correctamente" : "Actualizado correctamente",
            icon: "CheckIcon",
            variant: "success",
          },
        });

        if (!props.isAdd) {
          originalData.value = JSON.parse(JSON.stringify(itemData.value));
        }

        emit("refetch-data");
        closeWizard();
      })
      .catch((error) => {
        console.error("Error saving data:", error);
        toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            text: error.response?.data?.message || "Error al procesar la solicitud",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const getProfiles = () => {
    store
      .dispatch("dmp_profiles/getAll")
      .then((response) => {
        let { data } = response;
        profileOptions.value = data;
      })
      .catch((error) => {
        console.error("Error al obtener perfiles:", error);
      });
  };

  const changeCenter = (centerId) => {
    if (centerId) {
      store
        .dispatch("sector/getAll", {
          id_center: [centerId],
        })
        .then(() => {
          const sector = props.sectorOptions.filter(
            (item) => item.value === itemData.value.id_sector
          );

          if (sector.length === 0) {
            itemData.value.id_sector = null;
          }
        })
        .catch((error) => {
          console.error("Error al obtener sectores:", error);
        });
    }
  };

  const changedUnit = (units) => {
    const facilitiesFiltered = props.facilitiesOptions.filter(u => units.includes(u.value))
  
    additionalRouters.value = facilitiesFiltered.map(u => u.additional_routers_all).flat()
  }

  watch(
    () => itemData.value.id_center,
    (newCenter) => {
      if (newCenter) {
        changeCenter(newCenter);
      }
    }
  );

  watch(
    () => itemData.value.panel_model,
    (newPanelModel) => {
      if (newPanelModel) {
        getProfiles(newPanelModel);
      }
    }
  );

  return {
    currentStep,
    finishAtStep,
    isLoading,
    isCreatingPanel,
    isSyncingDevices,
    profileOptions,
    itemData,

    wizardTitle,
    wizardSteps,
    wizardStepIcons,
    hasChanges,

    resetData,
    closeWizard,
    prevStep,
    nextStep,
    loadData,
    handleSave,
    createItem,
    createItemAndSync,
    syncDevices,
    changeCenter,
    updatePanelBeforeSync,
    handleRetrySync,

    toast,
    t,
    changedUnit,
    additionalRouters,
  };
}
